import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs, limit, startAfter, orderBy } from "firebase/firestore";
import ProductCard from "../shared/ProductCard";
import { db } from "../firebaseConfig";
import { useProducts } from "../ProductContext";

const PRODUCTS_PER_PAGE = 12;

export default function ProductGrid({
  onCountChange,
  categoryName: categoryNameProp,
  filters = {},
  sortBy = "newest",
  searchQuery = ""
}) {
  const params = useParams();
  const { getCachedData, setCachedData, appendToCache } = useProducts();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Get category key
  const raw = categoryNameProp ?? params.name ?? params.categoryName ?? "";
  const cat = decodeURIComponent(raw || "");

  // Transform product data to only keep first image
  const transformProduct = (d) => {
    const data = d.data();
    const images = data.images ?? data.image;
    const firstImage = Array.isArray(images) ? images[0] : images;
    return {
      id: d.id,
      ...data,
      images: firstImage ? [firstImage] : [],
    };
  };

  // Initial load - check cache first
  useEffect(() => {
    let cancelled = false;

    async function load() {
      // Check if we have cached data
      const cached = getCachedData(cat);

      if (cached && cached.products.length > 0) {
        // Use cached data
        setProducts(cached.products);
        setLastDoc(cached.lastDoc);
        setHasMore(cached.hasMore);
        setLoading(false);
        onCountChange && onCountChange(cached.totalCount);
        return;
      }

      // No cache - fetch from Firestore
      setLoading(true);
      setError(null);
      setProducts([]);
      setHasMore(true);
      setLastDoc(null);

      try {
        const col = collection(db, "products");
        let q;

        if (cat) {
          q = query(col, where("category", "==", cat), orderBy("__name__"), limit(PRODUCTS_PER_PAGE));
        } else {
          q = query(col, orderBy("__name__"), limit(PRODUCTS_PER_PAGE));
        }

        const snap = await getDocs(q);
        if (cancelled) return;

        if (!snap.empty) {
          const arr = snap.docs.map(transformProduct);
          const newLastDoc = snap.docs[snap.docs.length - 1];
          const newHasMore = snap.docs.length === PRODUCTS_PER_PAGE;

          setProducts(arr);
          setLastDoc(newLastDoc);
          setHasMore(newHasMore);

          // Get total count for display
          const countQuery = cat
            ? query(col, where("category", "==", cat))
            : query(col);
          const countSnap = await getDocs(countQuery);
          const totalCount = countSnap.size;
          onCountChange && onCountChange(totalCount);

          // Save to cache
          setCachedData(cat, {
            products: arr,
            lastDoc: newLastDoc,
            hasMore: newHasMore,
            totalCount
          });
        } else {
          setHasMore(false);
          onCountChange && onCountChange(0);
          setCachedData(cat, { products: [], lastDoc: null, hasMore: false, totalCount: 0 });
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setError(e.message || "Failed to load products");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [cat, getCachedData, setCachedData, onCountChange]);

  // Load more products
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !lastDoc) return;

    setLoadingMore(true);

    try {
      const col = collection(db, "products");
      let q;

      if (cat) {
        q = query(col, where("category", "==", cat), orderBy("__name__"), startAfter(lastDoc), limit(PRODUCTS_PER_PAGE));
      } else {
        q = query(col, orderBy("__name__"), startAfter(lastDoc), limit(PRODUCTS_PER_PAGE));
      }

      const snap = await getDocs(q);

      if (!snap.empty) {
        const arr = snap.docs.map(transformProduct);
        const newLastDoc = snap.docs[snap.docs.length - 1];
        const newHasMore = snap.docs.length === PRODUCTS_PER_PAGE;

        setProducts(prev => [...prev, ...arr]);
        setLastDoc(newLastDoc);
        setHasMore(newHasMore);

        // Update cache
        appendToCache(cat, arr, newLastDoc, newHasMore);
      } else {
        setHasMore(false);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, lastDoc, cat, appendToCache]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: "100px" }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadingMore, loadMore]);

  // Sort products helper function
  const sortProducts = (productsToSort) => {
    const sorted = [...productsToSort];

    switch (sortBy) {
      case "price-low":
        return sorted.sort((a, b) => {
          const priceA = Number(a.offer) > 0 ? Math.round(Number(a.price) * (1 - Number(a.offer) / 100)) : Number(a.price);
          const priceB = Number(b.offer) > 0 ? Math.round(Number(b.price) * (1 - Number(b.offer) / 100)) : Number(b.price);
          return priceA - priceB;
        });
      case "price-high":
        return sorted.sort((a, b) => {
          const priceA = Number(a.offer) > 0 ? Math.round(Number(a.price) * (1 - Number(a.offer) / 100)) : Number(a.price);
          const priceB = Number(b.offer) > 0 ? Math.round(Number(b.price) * (1 - Number(b.offer) / 100)) : Number(b.price);
          return priceB - priceA;
        });
      case "name-asc":
        return sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      case "newest":
      default:
        return sorted;
    }
  };

  // Filter products
  const filteredProducts = sortProducts(products.filter(p => {
    const price = Number(p.price) || 0;
    const offer = Number(p.offer) || 0;
    const finalPrice = offer > 0 ? Math.round(price * (1 - offer / 100)) : price;

    if (filters.saleOnly && offer <= 0) return false;
    if (filters.min !== undefined && finalPrice < filters.min) return false;
    if (filters.max !== undefined && finalPrice > filters.max) return false;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchName = (p.name || "").toLowerCase().includes(q);
      const matchDesc = (p.description || "").toLowerCase().includes(q) || (p.shortDescription || "").toLowerCase().includes(q);
      if (!matchName && !matchDesc) return false;
    }

    return true;
  }));

  return (
    <div>
      {error && (
        <div className="error-message-box">
          {error}
        </div>
      )}

      {loading && (
        <div className="row row-cols-2 row-cols-sm-2 row-cols-md-3 row-cols-xl-4 g-3 g-md-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div className="col" key={i}>
              <div className="product-card">
                <div className="skeleton-img-placeholder" />
                <div className="skeleton-content">
                  <div className="skeleton skeleton-text-md" />
                  <div className="skeleton skeleton-text-sm" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && filteredProducts.length > 0 && (
        <>
          <div className="row row-cols-2 row-cols-sm-2 row-cols-md-3 row-cols-xl-4 g-3 g-md-4">
            {filteredProducts.map((p) => (
              <div key={p.id} className="col d-flex align-items-stretch">
                <ProductCard product={p} />
              </div>
            ))}
          </div>

          {/* Load more trigger element */}
          <div ref={loadMoreRef} className="load-more-trigger">
            {loadingMore && (
              <div className="load-more-spinner">
                <div className="spinner"></div>
                <span>Loading more products...</span>
              </div>
            )}
          </div>
        </>
      )}

      {!loading && filteredProducts.length === 0 && (
        <div>
          <div className="no-products-debug-box">
            <strong>No items</strong>
            <p className="debug-text">
              No products matched this category.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
